'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { DEFAULT_NUMBER_LOCALE, useFormatPrefsStore } from '@/shared/stores/format-prefs-store';
import { setNumberLocale } from '@/shared/utils/number-format';

const subscribe = (onChange: () => void) => useFormatPrefsStore.subscribe(onChange);
const getNumberFormat = () => useFormatPrefsStore.getState().numberFormat;
// Snapshot-ul de server/hidratare: pe server mereu formatul implicit (un singur store pentru toate
// cererile; formatul utilizatorului se știe doar în browser); în browser, valoarea deja citită din
// localStorage — cheia de la hidratare e cea salvată, deci nu se mai schimbă imediat după.
const getHydrationNumberFormat = () => (typeof window === 'undefined' ? DEFAULT_NUMBER_LOCALE : getNumberFormat());

/**
 * Aplică locale-ul de numere (din `useFormatPrefsStore`) în helper-ul
 * `number-format` și forțează re-randarea subtree-ului la schimbarea
 * preferinței prin `key={numberFormat}` (toggle rar în setări → remount
 * controlat al conținutului).
 *
 * `setNumberLocale` se apelează SINCRON la fiecare randare (nu doar în
 * useEffect) ca `activeLocale` să fie corect ÎNAINTE ca descendenții să
 * randeze numere. Apelul e idempotent (doar setează o variabilă de modul).
 *
 * Snapshot-ul de hidratare (al 3-lea argument) e, în browser, valoarea curentă, nu
 * `getInitialState()` ca în hook-ul zustand: persist a citit deja localStorage, deci cheia de la
 * hidratare e cea salvată și nu se mai schimbă imediat după — altfel, la formatul ro-RO, tot ce se
 * hidratase se remonta la fiecare încărcare (29.09.2026). Sigur cât timp serverul nu randează numere formatate
 * (acolo formatul utilizatorului nu e cunoscut): păzit de number-format-provider.spec.ts și de
 * alarma din number-format.ts; dacă s-ar întâmpla, React raportează eroare de hidratare și
 * re-randează porțiunea pe client.
 */
export function NumberFormatProvider({ children }: { children: React.ReactNode }) {
  const numberFormat = useSyncExternalStore(subscribe, getNumberFormat, getHydrationNumberFormat);

  // Sincron: descendenții randează cu locale-ul corect din prima.
  setNumberLocale(numberFormat);

  // Defensiv pentru cod care rulează după mount / în alt frame.
  useEffect(() => {
    setNumberLocale(numberFormat);
  }, [numberFormat]);

  return (
    <div key={numberFormat} style={{ display: 'contents' }}>
      {children}
    </div>
  );
}
